import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  TrendingUp,
  TrendingDown,
  Calendar,
  Building2,
  Users,
  FileText,
  BadgeIndianRupee,
  Layers,
  Percent,
  Search,
  Filter,
  ArrowUpDown,
  Download,
  HelpCircle,
  Package,
  Zap,
  Info,
} from "lucide-react";
import { StockItem } from "@/lib/accounting/types";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

function formatINR(v: number) {
  return `₹${(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

interface ProductPriceHistoryDialogProps {
  item: StockItem | null;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ProductPriceHistoryDialog({
  item,
  isOpen,
  onOpenChange,
}: ProductPriceHistoryDialogProps) {
  const [loading, setLoading] = useState<boolean>(true);
  const [analytics, setAnalytics] = useState<any>(null);
  const [filterType, setFilterType] = useState<string>("all");
  const [partyFilter, setPartyFilter] = useState<string>("");
  const [dateFilter, setDateFilter] = useState<string>("");

  useEffect(() => {
    if (!item?.id || !isOpen) return;

    let isMounted = true;
    setLoading(true);

    fetch(`/api/inventory/price-analytics/${item.id}`)
      .then((res) => res.json())
      .then((res) => {
        if (isMounted) {
          if (res.success) {
            setAnalytics(res);
          }
          setLoading(false);
        }
      })
      .catch((err) => {
        console.error("Failed to load price analytics:", err);
        if (isMounted) setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [item?.id, isOpen]);

  // Derived filtered transaction history
  const filteredHistory = useMemo(() => {
    if (!analytics?.history) return [];
    return analytics.history.filter((h: any) => {
      if (filterType !== "all" && h.transaction_type !== filterType) return false;
      if (partyFilter.trim() && !h.party_name?.toLowerCase().includes(partyFilter.toLowerCase().trim())) return false;
      if (dateFilter && !h.date.startsWith(dateFilter)) return false;
      return true;
    });
  }, [analytics?.history, filterType, partyFilter, dateFilter]);

  if (!item) return null;

  const isService = item.itemType === "service";
  const itemData = analytics?.item || item;
  const profitability = analytics?.profitability || {
    totalSaleQty: 0,
    totalRevenue: 0,
    totalCOGS: 0,
    totalGrossProfit: 0,
    grossMarginPercent: 0,
    markupPercent: 0,
  };
  const supplierMonthly = analytics?.supplierMonthly || [];
  const customerMonthly = analytics?.customerMonthly || [];
  const monthlyTrends = analytics?.monthlyTrends || [];

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto p-6 border-border shadow-2xl">
        <DialogHeader className="pb-3 border-b border-border/60">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <div>
              <div className="flex items-center gap-2.5">
                <DialogTitle className="text-xl font-bold font-display text-foreground">
                  {item.name}
                </DialogTitle>
                <Badge
                  variant={isService ? "secondary" : "default"}
                  className="text-[10px] uppercase font-semibold"
                >
                  {isService ? "Service" : "Goods"}
                </Badge>
                {item.stockGroup && (
                  <Badge variant="outline" className="text-[10px] text-muted-foreground">
                    {item.stockGroup}
                  </Badge>
                )}
              </div>
              <DialogDescription className="text-xs text-muted-foreground mt-0.5">
                Cost & Selling Price Intelligence · Weighted Average Costing (WAC) · Supplier Variance
              </DialogDescription>
            </div>
            <div className="text-right">
              <span className="text-xs text-muted-foreground">Stock Valuation: </span>
              <strong className="text-sm font-mono font-bold text-foreground">
                {formatINR(itemData.currentValue || 0)}
              </strong>
            </div>
          </div>
        </DialogHeader>

        {/* 1. Core Price Intelligence KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 my-4">
          {/* Average Purchase Cost (WAC) */}
          <div className="p-3.5 rounded-xl border border-blue-500/20 bg-blue-500/5 space-y-1">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-semibold text-blue-600 dark:text-blue-400 uppercase tracking-wider">
                Average Purchase Cost
              </span>
              <TooltipProvider delayDuration={150}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="h-3 w-3 text-blue-500/70 cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs text-xs">
                    Maintained via Weighted Average Cost (WAC) across all chronological purchase inflows and returns.
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <div className="text-xl font-extrabold font-mono text-foreground">
              {formatINR(itemData.currentRate || 0)}
            </div>
            <p className="text-[10px] text-muted-foreground">
              Method: Weighted Average Cost
            </p>
          </div>

          {/* Last Purchase Rate */}
          <div className="p-3.5 rounded-xl border border-border bg-card space-y-1">
            <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
              Last Purchase Rate
            </span>
            <div className="text-xl font-extrabold font-mono text-foreground">
              {itemData.lastPurchaseRate !== null && itemData.lastPurchaseRate !== undefined
                ? formatINR(itemData.lastPurchaseRate)
                : <span className="text-xs text-muted-foreground font-sans">Not Purchased Yet</span>}
            </div>
            <p className="text-[10px] text-muted-foreground truncate" title={itemData.lastPurchaseParty || ""}>
              {itemData.lastPurchaseParty
                ? `${itemData.lastPurchaseParty} (${itemData.lastPurchaseDate || ""})`
                : "No historical purchase"}
            </p>
          </div>

          {/* Last Selling Rate */}
          <div className="p-3.5 rounded-xl border border-emerald-500/20 bg-emerald-500/5 space-y-1">
            <span className="text-[11px] font-semibold text-emerald-600 dark:text-emerald-400 uppercase tracking-wider">
              Last Actual Selling Rate
            </span>
            <div className="text-xl font-extrabold font-mono text-emerald-600 dark:text-emerald-400">
              {itemData.lastSellingRate !== null && itemData.lastSellingRate !== undefined
                ? formatINR(itemData.lastSellingRate)
                : <span className="text-xs text-amber-600 dark:text-amber-400 font-medium">Not Sold Yet</span>}
            </div>
            <p className="text-[10px] text-muted-foreground truncate" title={itemData.lastSoldParty || ""}>
              {itemData.lastSoldParty
                ? `Sold to ${itemData.lastSoldParty} (${itemData.lastSoldDate || ""})`
                : "Awaiting first sales invoice"}
            </p>
          </div>

          {/* Gross Margin & Markup */}
          <div className="p-3.5 rounded-xl border border-purple-500/20 bg-purple-500/5 space-y-1">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-semibold text-purple-600 dark:text-purple-400 uppercase tracking-wider">
                Gross Margin & Markup
              </span>
              <TooltipProvider delayDuration={150}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="h-3 w-3 text-purple-500/70 cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs text-xs">
                    <p className="font-semibold mb-1">Difference between Margin & Markup:</p>
                    <p>• Margin: Profit ÷ Revenue ({profitability.grossMarginPercent}%)</p>
                    <p>• Markup: Profit ÷ Cost ({profitability.markupPercent}%)</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-xl font-extrabold font-mono text-purple-600 dark:text-purple-400">
                {profitability.grossMarginPercent}%
              </span>
              <span className="text-xs text-muted-foreground font-mono">
                ({profitability.markupPercent}% mkp)
              </span>
            </div>
            <p className="text-[10px] text-muted-foreground">
              Profit: {formatINR(profitability.totalGrossProfit)}
            </p>
          </div>
        </div>

        {/* 2. Range Summary Bar */}
        <div className="px-3.5 py-2.5 rounded-lg bg-muted/40 border border-border/60 flex flex-wrap items-center justify-between gap-3 text-xs mb-4">
          <div className="flex items-center gap-4">
            <div>
              <span className="text-muted-foreground text-[11px]">Purchase Price Range: </span>
              <span className="font-mono font-semibold">
                {itemData.lowestPurchaseRate !== null ? formatINR(itemData.lowestPurchaseRate) : "—"}
                {" – "}
                {itemData.highestPurchaseRate !== null ? formatINR(itemData.highestPurchaseRate) : "—"}
              </span>
            </div>
            <div className="h-3 w-px bg-border hidden sm:block" />
            <div>
              <span className="text-muted-foreground text-[11px]">Selling Price Range: </span>
              <span className="font-mono font-semibold">
                {itemData.lowestSellingRate !== null ? formatINR(itemData.lowestSellingRate) : "—"}
                {" – "}
                {itemData.highestSellingRate !== null ? formatINR(itemData.highestSellingRate) : "—"}
              </span>
            </div>
          </div>
          <div>
            <span className="text-muted-foreground text-[11px]">Current Physical Stock: </span>
            <strong className="font-semibold">{itemData.currentQty} {itemData.unit}</strong>
          </div>
        </div>

        {/* 3. Interactive Intelligence Tabs */}
        <Tabs defaultValue="supplier-comparison" className="w-full">
          <TabsList className="grid grid-cols-3 mb-4 w-full sm:w-[480px]">
            <TabsTrigger value="supplier-comparison" className="text-xs">
              Monthly Comparison
            </TabsTrigger>
            <TabsTrigger value="transaction-history" className="text-xs">
              Transaction History ({analytics?.history?.length || 0})
            </TabsTrigger>
            <TabsTrigger value="profitability" className="text-xs">
              Profitability Breakdown
            </TabsTrigger>
          </TabsList>

          {/* TAB 1: Monthly Supplier & Customer Comparison */}
          <TabsContent value="supplier-comparison" className="space-y-5">
            {/* Supplier Monthly Comparison Table */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-1.5">
                  <Building2 className="h-4 w-4 text-primary" />
                  <h4 className="text-xs font-bold uppercase tracking-wider text-foreground">
                    Monthly Purchase Cost Comparison by Supplier
                  </h4>
                </div>
                <span className="text-[11px] text-muted-foreground">
                  Track price changes from each vendor month-over-month
                </span>
              </div>

              {supplierMonthly.length === 0 ? (
                <div className="p-6 text-center rounded-xl bg-muted/20 border border-border/50 text-xs text-muted-foreground">
                  No historical purchases recorded for this item.
                </div>
              ) : (
                <div className="border border-border/80 rounded-xl overflow-hidden shadow-xs">
                  <table className="w-full text-left border-collapse text-xs">
                    <thead>
                      <tr className="bg-muted/70 border-b border-border text-[11px] font-bold text-muted-foreground uppercase tracking-wider">
                        <th className="py-2.5 px-3">Month</th>
                        <th className="py-2.5 px-3">Supplier Name</th>
                        <th className="py-2.5 px-3">Invoice Ref</th>
                        <th className="py-2.5 px-3">Date</th>
                        <th className="py-2.5 px-3 text-right">Quantity</th>
                        <th className="py-2.5 px-3 text-right">Purchase Rate</th>
                        <th className="py-2.5 px-3 text-right">Total Value</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border/50">
                      {supplierMonthly.map((row: any, i: number) => {
                        const avgCost = itemData.currentRate || 0;
                        const diff = row.purchase_rate - avgCost;
                        return (
                          <tr key={i} className="hover:bg-muted/30 transition-colors">
                            <td className="py-2.5 px-3 font-semibold font-mono text-foreground">
                              {row.month_year}
                            </td>
                            <td className="py-2.5 px-3 font-medium text-foreground">
                              {row.supplier_name}
                            </td>
                            <td className="py-2.5 px-3 font-mono text-muted-foreground">
                              {row.voucher_number || row.invoice_reference}
                            </td>
                            <td className="py-2.5 px-3 text-muted-foreground">
                              {row.date}
                            </td>
                            <td className="py-2.5 px-3 text-right font-mono font-medium">
                              {row.quantity} {item.unit}
                            </td>
                            <td className="py-2.5 px-3 text-right font-mono font-bold">
                              <span>{formatINR(row.purchase_rate)}</span>
                              {diff !== 0 && (
                                <span
                                  className={`ml-1.5 text-[10px] font-medium ${
                                    diff > 0 ? "text-rose-500" : "text-emerald-500"
                                  }`}
                                  title={`Difference from Avg Cost (${formatINR(avgCost)})`}
                                >
                                  {diff > 0 ? `+₹${diff.toFixed(2)}` : `-₹${Math.abs(diff).toFixed(2)}`}
                                </span>
                              )}
                            </td>
                            <td className="py-2.5 px-3 text-right font-mono text-muted-foreground">
                              {formatINR(row.total_amount)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Customer Monthly Actual Selling Rates */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-1.5">
                  <Users className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                  <h4 className="text-xs font-bold uppercase tracking-wider text-foreground">
                    Actual Selling Rates by Month & Customer
                  </h4>
                </div>
                <span className="text-[11px] text-muted-foreground">
                  Actual invoice transaction rates and realized gross profits
                </span>
              </div>

              {customerMonthly.length === 0 ? (
                <div className="p-6 text-center rounded-xl bg-amber-500/5 border border-amber-500/20 text-xs text-amber-700 dark:text-amber-300">
                  This product has not been sold yet. When a sales invoice is created, actual selling rates and profit margins will appear here.
                </div>
              ) : (
                <div className="border border-border/80 rounded-xl overflow-hidden shadow-xs">
                  <table className="w-full text-left border-collapse text-xs">
                    <thead>
                      <tr className="bg-muted/70 border-b border-border text-[11px] font-bold text-muted-foreground uppercase tracking-wider">
                        <th className="py-2.5 px-3">Month</th>
                        <th className="py-2.5 px-3">Customer Name</th>
                        <th className="py-2.5 px-3">Invoice Ref</th>
                        <th className="py-2.5 px-3">Date</th>
                        <th className="py-2.5 px-3 text-right">Qty</th>
                        <th className="py-2.5 px-3 text-right">Selling Rate</th>
                        <th className="py-2.5 px-3 text-right">Unit Cost</th>
                        <th className="py-2.5 px-3 text-right">Gross Profit</th>
                        <th className="py-2.5 px-3 text-right">Margin %</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border/50">
                      {customerMonthly.map((row: any, i: number) => (
                        <tr key={i} className="hover:bg-muted/30 transition-colors">
                          <td className="py-2.5 px-3 font-semibold font-mono text-foreground">
                            {row.month_year}
                          </td>
                          <td className="py-2.5 px-3 font-medium text-foreground">
                            {row.customer_name}
                          </td>
                          <td className="py-2.5 px-3 font-mono text-muted-foreground">
                            {row.voucher_number || row.invoice_reference}
                          </td>
                          <td className="py-2.5 px-3 text-muted-foreground">
                            {row.date}
                          </td>
                          <td className="py-2.5 px-3 text-right font-mono font-medium">
                            {row.quantity}
                          </td>
                          <td className="py-2.5 px-3 text-right font-mono font-bold text-emerald-600 dark:text-emerald-400">
                            {formatINR(row.selling_rate)}
                          </td>
                          <td className="py-2.5 px-3 text-right font-mono text-muted-foreground">
                            {formatINR(row.unit_cost)}
                          </td>
                          <td className="py-2.5 px-3 text-right font-mono font-bold text-foreground">
                            {formatINR(row.gross_profit)}
                          </td>
                          <td className="py-2.5 px-3 text-right font-mono font-semibold text-purple-600 dark:text-purple-400">
                            {row.gross_margin_percent}%
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </TabsContent>

          {/* TAB 2: Full Filterable Transaction History */}
          <TabsContent value="transaction-history" className="space-y-3">
            {/* Filter Bar */}
            <div className="flex flex-wrap items-center gap-2 bg-muted/30 p-2.5 rounded-xl border border-border/50">
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Filter className="h-3.5 w-3.5" />
                <span>Filters:</span>
              </div>
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="w-36 h-8 text-xs bg-background">
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Transactions</SelectItem>
                  <SelectItem value="purchase">Purchases Only</SelectItem>
                  <SelectItem value="sale">Sales Only</SelectItem>
                  <SelectItem value="sales_return">Sales Returns</SelectItem>
                  <SelectItem value="purchase_return">Purchase Returns</SelectItem>
                </SelectContent>
              </Select>
              <div className="relative flex-1 min-w-[160px]">
                <Search className="absolute left-2.5 top-2.5 h-3 w-3 text-muted-foreground" />
                <Input
                  placeholder="Filter supplier/customer..."
                  value={partyFilter}
                  onChange={(e) => setPartyFilter(e.target.value)}
                  className="h-8 pl-8 text-xs bg-background"
                />
              </div>
              <Input
                type="month"
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                className="w-36 h-8 text-xs bg-background"
                title="Filter by month"
              />
              {(filterType !== "all" || partyFilter || dateFilter) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setFilterType("all");
                    setPartyFilter("");
                    setDateFilter("");
                  }}
                  className="h-8 text-xs text-muted-foreground hover:text-foreground"
                >
                  Reset
                </Button>
              )}
            </div>

            {/* Full History Table */}
            <div className="border border-border/80 rounded-xl overflow-hidden shadow-xs">
              <table className="w-full text-left border-collapse text-xs">
                <thead>
                  <tr className="bg-muted/70 border-b border-border text-[11px] font-bold text-muted-foreground uppercase tracking-wider">
                    <th className="py-2 px-3">Date</th>
                    <th className="py-2 px-3">Type</th>
                    <th className="py-2 px-3">Invoice #</th>
                    <th className="py-2 px-3">Party (Supplier / Customer)</th>
                    <th className="py-2 px-3 text-right">Qty</th>
                    <th className="py-2 px-3 text-right">Unit Rate</th>
                    <th className="py-2 px-3 text-right">Taxable</th>
                    <th className="py-2 px-3 text-right">GST</th>
                    <th className="py-2 px-3 text-right">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/50">
                  {filteredHistory.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="py-8 text-center text-xs text-muted-foreground">
                        No transactions match your selected filter criteria.
                      </td>
                    </tr>
                  ) : (
                    filteredHistory.map((row: any, i: number) => {
                      const isSale = row.transaction_type === "sale";
                      const isPurchase = row.transaction_type === "purchase";
                      return (
                        <tr key={i} className="hover:bg-muted/30 transition-colors">
                          <td className="py-2 px-3 font-mono text-muted-foreground">
                            {row.date}
                          </td>
                          <td className="py-2 px-3">
                            <Badge
                              variant="outline"
                              className={`text-[9px] uppercase font-semibold px-1.5 py-0.2 ${
                                isSale
                                  ? "bg-emerald-500/10 text-emerald-600 border-emerald-500/30"
                                  : isPurchase
                                  ? "bg-blue-500/10 text-blue-600 border-blue-500/30"
                                  : "bg-amber-500/10 text-amber-600 border-amber-500/30"
                              }`}
                            >
                              {row.transaction_type.replace("_", " ")}
                            </Badge>
                          </td>
                          <td className="py-2 px-3 font-mono text-foreground font-medium">
                            {row.voucher_number}
                          </td>
                          <td className="py-2 px-3 font-medium text-foreground">
                            {row.party_name}
                          </td>
                          <td className="py-2 px-3 text-right font-mono">
                            {row.quantity} {row.unit}
                          </td>
                          <td className={`py-2 px-3 text-right font-mono font-bold ${
                            isSale ? "text-emerald-600 dark:text-emerald-400" : "text-foreground"
                          }`}>
                            {formatINR(row.unit_rate)}
                          </td>
                          <td className="py-2 px-3 text-right font-mono text-muted-foreground">
                            {formatINR(row.taxable_amount)}
                          </td>
                          <td className="py-2 px-3 text-right font-mono text-muted-foreground">
                            {row.gst_rate}%
                          </td>
                          <td className="py-2 px-3 text-right font-mono font-semibold text-foreground">
                            {formatINR(row.total_amount)}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* TAB 3: Profitability Analysis */}
          <TabsContent value="profitability" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="p-4 rounded-xl border border-border bg-card">
                <span className="text-[11px] font-semibold text-muted-foreground uppercase">Total Sales Revenue</span>
                <div className="text-xl font-bold font-mono text-foreground mt-1">
                  {formatINR(profitability.totalRevenue)}
                </div>
                <p className="text-[10px] text-muted-foreground mt-0.5">Total units sold: {profitability.totalSaleQty} {item.unit}</p>
              </div>

              <div className="p-4 rounded-xl border border-border bg-card">
                <span className="text-[11px] font-semibold text-muted-foreground uppercase">Cost of Goods Sold (COGS)</span>
                <div className="text-xl font-bold font-mono text-foreground mt-1">
                  {formatINR(profitability.totalCOGS)}
                </div>
                <p className="text-[10px] text-muted-foreground mt-0.5">Assigned via Weighted Average Cost</p>
              </div>

              <div className="p-4 rounded-xl border border-emerald-500/30 bg-emerald-500/5">
                <span className="text-[11px] font-semibold text-emerald-600 dark:text-emerald-400 uppercase">Gross Profit Realized</span>
                <div className="text-xl font-bold font-mono text-emerald-600 dark:text-emerald-400 mt-1">
                  {formatINR(profitability.totalGrossProfit)}
                </div>
                <p className="text-[10px] text-muted-foreground mt-0.5">
                  Margin: {profitability.grossMarginPercent}% · Markup: {profitability.markupPercent}%
                </p>
              </div>
            </div>

            {/* Formula Explanation Card */}
            <div className="p-4 rounded-xl border border-border/80 bg-muted/20 text-xs space-y-2">
              <h5 className="font-bold flex items-center gap-1.5 text-foreground">
                <HelpCircle className="h-4 w-4 text-primary" />
                Accounting Costing & Margin Methodology
              </h5>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-[11px] text-muted-foreground">
                <div className="p-2.5 rounded-lg bg-background border border-border/60">
                  <strong className="text-foreground block mb-0.5">Gross Margin Formula:</strong>
                  <code>Gross Margin (%) = ((Revenue − COGS) ÷ Revenue) × 100</code>
                  <p className="mt-1 text-[10px]">
                    Represents what percentage of total sales is retained as profit after direct purchase costs.
                  </p>
                </div>
                <div className="p-2.5 rounded-lg bg-background border border-border/60">
                  <strong className="text-foreground block mb-0.5">Markup Formula:</strong>
                  <code>Markup (%) = ((Selling Price − Purchase Cost) ÷ Purchase Cost) × 100</code>
                  <p className="mt-1 text-[10px]">
                    Represents the percentage added on top of the product's actual purchase cost to set selling price.
                  </p>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
